import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  Card,
  List,
  Badge,
  Avatar,
  Typography,
  Input,
  Collapse,
  Tag,
  Empty,
  Spin,
} from "antd";
import {
  MessageOutlined,
  UserOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useChat } from "src/shared/hooks/useChat";
import {
  selectRoomsGroupedByCase,
  selectIsLoadingRooms,
  selectPinnedCaseId,
} from "src/domain/app/redux/chat/chat-selectors";
import { getMyProfile } from "src/domain/app/redux/auth/auth-selectors";
import { RoomType } from "src/shared/services/chat/dto";
import { UserType } from "src/shared/constants/AppConst";
import moment from "moment";
import "./ChatRoomListPopup.less";

const { Text } = Typography;
const { Panel } = Collapse;

interface ChatRoomListPopupProps {
  onSelectRoom: (roomId: string) => void;
  onClose: () => void;
}

const ChatRoomListPopup: React.FC<ChatRoomListPopupProps> = ({
  onSelectRoom,
  onClose,
}) => {
  const { messages } = useIntl();
  const { loadRooms } = useChat();
  const roomsGroupedByCase = useSelector(selectRoomsGroupedByCase);
  const isLoadingRooms = useSelector(selectIsLoadingRooms);
  const myProfile = useSelector(getMyProfile);
  const pinnedCaseId = useSelector(selectPinnedCaseId);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    loadRooms();
  }, []);

  const getRoomTypeLabel = (roomType: RoomType) => {
    switch (roomType) {
      case RoomType.CoordinatorDoctor:
        return messages["chat.doctor"] as string;
      case RoomType.CoordinatorPlanner:
        return messages["chat.planner"] as string;
      case RoomType.CoordinatorFabricator:
        return messages["chat.fabricator"] as string;
      default:
        return "";
    }
  };

  const getRoomTypeColor = (roomType: RoomType) => {
    switch (roomType) {
      case RoomType.CoordinatorDoctor:
        return "blue";
      case RoomType.CoordinatorPlanner:
        return "green";
      case RoomType.CoordinatorFabricator:
        return "orange";
      default:
        return "default";
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = moment(dateStr);
    const now = moment();

    if (date.isSame(now, "day")) {
      return date.format("hh:mm A");
    } else if (date.isSame(now.subtract(1, "day"), "day")) {
      return messages["chat.yesterday"] as string;
    } else {
      return date.format("MMM DD");
    }
  };

  const isCoordinator = myProfile?.userType === UserType.Coordinator;

  const filteredGroupedRooms = searchText
    ? roomsGroupedByCase.filter(
        (group) =>
          group.caseLabel?.toLowerCase().includes(searchText.toLowerCase()) ||
          group.rooms.some(
            (room) =>
              (isCoordinator
                ? room.otherParticipantName
                    .toLowerCase()
                    .includes(searchText.toLowerCase())
                : room.coordinatorName
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
              room.title.toLowerCase().includes(searchText.toLowerCase()) ||
              room.subtitle?.toLowerCase().includes(searchText.toLowerCase()),
          ),
      )
    : roomsGroupedByCase;

  return (
    <Card className="chat-room-list-popup" bordered={false}>
      <div className="popup-header">
        <Text strong>{messages["chat.title"] as string}</Text>
        <CloseOutlined onClick={onClose} className="close-icon" />
      </div>

      <div className="popup-search">
        <Input
          placeholder={messages["chat.search"] as string}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          size="small"
        />
      </div>

      <div className="popup-content">
        {isLoadingRooms ? (
          <div className="loading-container">
            <Spin />
          </div>
        ) : filteredGroupedRooms.length === 0 ? (
          <Empty
            description={messages["chat.noRooms"] as string}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : isCoordinator ? (
          <Collapse
            defaultActiveKey={filteredGroupedRooms.map((g) => g.caseId)}
            ghost
            className="case-collapse"
          >
            {filteredGroupedRooms.map((group) => (
              <Panel
                key={group.caseId}
                className={group.caseId === pinnedCaseId ? "pinned-panel" : ""}
                header={
                  <div className="case-header">
                    <Text strong>{group.caseLabel}</Text>
                    <Badge
                      count={group.rooms.reduce(
                        (sum, r) => sum + r.unreadCount,
                        0,
                      )}
                      overflowCount={99}
                    />
                  </div>
                }
              >
                <List
                  dataSource={group.rooms}
                  renderItem={(room) => (
                    <List.Item
                      className={`room-item ${group.caseId === pinnedCaseId ? "pinned-room" : ""}`}
                      onClick={() => onSelectRoom(room.id)}
                    >
                      <List.Item.Meta
                        avatar={
                          <Badge count={room.unreadCount} offset={[-5, 5]}>
                            <Avatar icon={<MessageOutlined />} size="small" />
                          </Badge>
                        }
                        title={
                          <div className="room-title">
                            <Text strong>{room.otherParticipantName}</Text>
                            <Text className="room-time">
                              {formatTime(room.lastMessageDate)}
                            </Text>
                          </div>
                        }
                        description={
                          <>
                            {room.subtitle && (
                              <Text type="secondary" className="room-subtitle">
                                {room.subtitle}
                                <br />
                              </Text>
                            )}
                            <Text ellipsis className="last-message">
                              {room.lastMessage ||
                                (messages["chat.noMessages"] as string)}
                            </Text>
                          </>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Panel>
            ))}
          </Collapse>
        ) : (
          <List
            dataSource={filteredGroupedRooms.flatMap((g) => g.rooms)}
            renderItem={(room) => (
              <List.Item
                className="room-item"
                onClick={() => onSelectRoom(room.id)}
              >
                <List.Item.Meta
                  avatar={
                    <Badge count={room.unreadCount} offset={[-5, 5]}>
                      <Avatar icon={<UserOutlined />} size="small" />
                    </Badge>
                  }
                  title={
                    <div className="room-title">
                      <Text strong>{room.otherParticipantName}</Text>
                      <Text className="room-time">
                        {formatTime(room.lastMessageDate)}
                      </Text>
                    </div>
                  }
                  description={
                    <>
                      {room.subtitle && (
                        <Text type="secondary" className="room-subtitle">
                          {room.subtitle}
                          <br />
                        </Text>
                      )}
                      <Text ellipsis className="last-message">
                        {room.lastMessage ||
                          (messages["chat.noMessages"] as string)}
                      </Text>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </Card>
  );
};

export default ChatRoomListPopup;
